// src/components/teacher/TeacherCampusInfo.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import {
  BookIcon,
  CampusIcon,
  CalendarIcon,
  LocationIcon,
  MailIcon,
  PhoneIcon,
  UsersIcon,
} from "../shared/icons/Icons";
import {
  USE_DUMMY_DATA,
  DUMMY_CAMPUS,
  DUMMY_COURSES,
  DUMMY_STUDENTS,
  DUMMY_TIMETABLE,
} from "./dummyData";

function TeacherCampusInfo({ teacherData }) {
  const navigate = useNavigate();
  const [campusInfo, setCampusInfo] = useState(
    USE_DUMMY_DATA ? DUMMY_CAMPUS : null,
  );
  const [summary, setSummary] = useState({
    courses: USE_DUMMY_DATA ? DUMMY_COURSES : [],
    students: USE_DUMMY_DATA ? DUMMY_STUDENTS : [],
    timetable: USE_DUMMY_DATA ? DUMMY_TIMETABLE : [],
  });
  const [loading, setLoading] = useState(!USE_DUMMY_DATA);

  useEffect(() => {
    if (USE_DUMMY_DATA) {
      setLoading(false);
      return;
    }

    const fetchCampusInfo = async () => {
      try {
        const campusId =
          teacherData?.campus_id ||
          localStorage.getItem("als_teacher_campus_id");

        if (!campusId) {
          setLoading(false);
          return;
        }

        const headers = {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        };

        const [campusRes, coursesRes, studentsRes, timetableRes] =
          await Promise.all([
            fetch(`${API_BASE_URL}/api/campuses/${campusId}`, { headers }),
            fetch(`${API_BASE_URL}/api/teachers/me/courses`, { headers }),
            fetch(`${API_BASE_URL}/api/teachers/me/students`, { headers }),
            fetch(`${API_BASE_URL}/api/teachers/me/timetable`, { headers }),
          ]);

        if (!campusRes.ok) {
          throw new Error("Failed to fetch campus info");
        }

        setCampusInfo(await campusRes.json());
        setSummary({
          courses: coursesRes.ok ? await coursesRes.json() : [],
          students: studentsRes.ok ? await studentsRes.json() : [],
          timetable: timetableRes.ok ? await timetableRes.json() : [],
        });
      } catch (err) {
        console.error("Failed to fetch campus info:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCampusInfo();
  }, [teacherData]);

  if (loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading campus information...</p>
      </div>
    );
  }

  const assignedClasses = new Set(
    summary.students
      .map((student) =>
        [student.class_name, student.class_section].filter(Boolean).join(" "),
      )
      .filter(Boolean),
  );

  return (
    <div>
      <div className="tp-page-header">
        <div>
        </div>
        <button
          type="button"
          className="tp-btn tp-btn--ghost"
          onClick={() => navigate("/teacher/overview")}
        >
          Back to Dashboard
        </button>
      </div>

      <div className="tp-card">
        {campusInfo ? (
          <>
            <div className="tp-page-header">
              <div>
                <h2>{campusInfo.name}</h2>
                <p>
                  {campusInfo.principal
                    ? `Principal: ${campusInfo.principal}`
                    : "Campus details"}
                </p>
              </div>
              <span className="tp-badge">ID: {campusInfo.id}</span>
            </div>

            <div className="tp-info-grid" style={{ marginBottom: "1.75rem" }}>
              <div className="tp-info-item">
                <LocationIcon />
                <div>
                  <label>Address</label>
                  <span>
                    {campusInfo.address || campusInfo.location || "N/A"}
                  </span>
                </div>
              </div>
              <div className="tp-info-item">
                <LocationIcon />
                <div>
                  <label>City</label>
                  <span>{campusInfo.city || "N/A"}</span>
                </div>
              </div>
              <div className="tp-info-item">
                <PhoneIcon />
                <div>
                  <label>Contact</label>
                  <span>{campusInfo.contact || "N/A"}</span>
                </div>
              </div>
              <div className="tp-info-item">
                <MailIcon />
                <div>
                  <label>Email</label>
                  <span>{campusInfo.email || "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="tp-stats-grid">
              <button
                type="button"
                className="tp-card tp-card--clickable"
                onClick={() => navigate("/teacher/students")}
              >
                <div className="tp-stat-top">
                  <div
                    className="tp-stat-icon"
                    style={{
                      background: "rgba(29,78,216,0.1)",
                      color: "#1d4ed8",
                    }}
                  >
                    <UsersIcon />
                  </div>
                </div>
                <h3 className="tp-stat-value">
                  {summary.students.length || campusInfo.total_students || "-"}
                </h3>
                <p className="tp-stat-label">My Students</p>
              </button>

              <button
                type="button"
                className="tp-card tp-card--clickable"
                onClick={() => navigate("/teacher/courses")}
              >
                <div className="tp-stat-top">
                  <div
                    className="tp-stat-icon"
                    style={{
                      background: "rgba(22,163,74,0.1)",
                      color: "#16a34a",
                    }}
                  >
                    <BookIcon />
                  </div>
                </div>
                <h3 className="tp-stat-value">{summary.courses.length}</h3>
                <p className="tp-stat-label">My Courses</p>
              </button>

              <div className="tp-card">
                <div className="tp-stat-top">
                  <div
                    className="tp-stat-icon"
                    style={{
                      background: "rgba(124,58,237,0.1)",
                      color: "#7c3aed",
                    }}
                  >
                    <CampusIcon />
                  </div>
                </div>
                <h3 className="tp-stat-value">{assignedClasses.size}</h3>
                <p className="tp-stat-label">Assigned Classes</p>
              </div>

              <button
                type="button"
                className="tp-card tp-card--clickable"
                onClick={() => navigate("/teacher/timetable")}
              >
                <div className="tp-stat-top">
                  <div
                    className="tp-stat-icon"
                    style={{
                      background: "rgba(184,134,11,0.12)",
                      color: "#b8860b",
                    }}
                  >
                    <CalendarIcon />
                  </div>
                </div>
                <h3 className="tp-stat-value">{summary.timetable.length}</h3>
                <p className="tp-stat-label">Timetable Slots</p>
              </button>
            </div>
          </>
        ) : (
          <div className="tp-empty" style={{ border: "none", boxShadow: "none" }}>
            <CampusIcon />
            <h3>No campus information</h3>
            <p>Your campus details are not available at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherCampusInfo;
